import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  ArrowLeft,
  ShoppingBag,
  Trash2,
  Plus,
  Minus,
  MapPin,
  Phone,
  Clock,
  Check,
  Loader2,
  AlertCircle,
  Pill,
  Send,
  Navigation
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PharmacyCartPage = ({ 
  onNavigate, 
  cart = [], 
  onUpdateCart, 
  onClearCart 
}) => {
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reservationId, setReservationId] = useState(null);
  
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    phone: '',
    email: '',
    notes: ''
  });

  const logoUrl = 'https://customer-assets.emergentagent.com/job_skincare-check-1/artifacts/6cemh04i_LOGO%20DEF.jpg';

  const pharmacyInfo = {
    name: 'Pharmacie Saint Ange Des Amis',
    address: 'Angre 7e tranche carrefour Bluetooth, Abidjan, Côte d\'Ivoire',
    phone: '07 78 41 52 77',
    gpsCode: '92V7+H8',
    mapsUrl: 'https://www.google.com/maps/place/92V7%2BH8+Abidjan,+C%C3%B4te+d\'Ivoire',
    hours: {
      weekdays: '7h30 - 20h00',
      saturday: '7h30 - 13h00'
    }
  };

  const updateQuantity = (productId, delta) => {
    const item = cart.find(i => i.id === productId);
    if (!item) return;
    
    const newQuantity = item.quantity + delta;
    if (newQuantity <= 0) {
      onUpdateCart(cart.filter(i => i.id !== productId));
    } else {
      onUpdateCart(cart.map(i => 
        i.id === productId ? { ...i, quantity: newQuantity } : i
      ));
    }
  };

  const removeItem = (productId) => {
    onUpdateCart(cart.filter(i => i.id !== productId));
    toast.success('Produit retiré de la demande');
  };

  const totalAmount = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);

  const handleSubmitReservation = async () => {
    if (!customerInfo.name || !customerInfo.phone) {
      toast.error('Veuillez remplir votre nom et numéro de téléphone');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/pharmacy/reservations`, {
        customer_name: customerInfo.name,
        customer_phone: customerInfo.phone,
        customer_email: customerInfo.email,
        notes: customerInfo.notes,
        items: cart.map(item => ({
          product_id: item.id,
          product_name: item.name,
          quantity: item.quantity,
          unit_price: item.price
        })),
        total_amount: totalAmount
      });

      setReservationId(response.data.id);
      setSuccessDialogOpen(true);
      setConfirmDialogOpen(false);
      onClearCart();
      
      // Show email status
      if (response.data.email_sent) {
        toast.success('Demande de mise à disposition envoyée. La pharmacie a été notifiée par email.');
      } else {
        toast.success('Demande de mise à disposition enregistrée. La pharmacie vérifiera bientôt.');
      }
    } catch (error) {
      console.error('Error submitting reservation:', error);
      toast.error('Erreur lors de l\'envoi de la demande. Veuillez réessayer ou nous appeler.');
    } finally {
      setSubmitting(false);
    }
  };

  if (cart.length === 0 && !successDialogOpen) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#FDF8F3] to-white">
        {/* Header */}
        <header className="bg-white/90 backdrop-blur-md border-b border-[#E8DDD4] sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 py-3">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onNavigate('home')}
                className="text-[#8B7355]"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <img 
                src={logoUrl} 
                alt="Logo" 
                className="h-8 w-auto object-contain"
              />
              <h1 className="font-bold text-[#5D4E37] text-lg">Ma demande</h1>
            </div>
          </div>
        </header>

        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <div className="w-24 h-24 bg-[#C45C26]/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <ShoppingBag className="w-12 h-12 text-[#C45C26]/50" />
          </div>
          <h2 className="text-xl font-semibold text-[#5D4E37] mb-2">Aucun produit en attente</h2>
          <p className="text-[#8B7355] mb-6">
            Parcourez notre catalogue et ajoutez des produits à réserver
          </p>
          <Button
            onClick={() => onNavigate('catalog')}
            className="bg-[#C45C26] hover:bg-[#A84B1F]"
          >
            Parcourir le catalogue
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FDF8F3] to-white">
      {/* Header */}
      <header className="bg-white/90 backdrop-blur-md border-b border-[#E8DDD4] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onNavigate('catalog')}
                className="text-[#8B7355]"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <img 
                src={logoUrl} 
                alt="Logo" 
                className="h-8 w-auto object-contain"
              />
              <h1 className="font-bold text-[#5D4E37] text-lg">
                Ma demande ({totalItems} article{totalItems > 1 ? 's' : ''})
              </h1>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {/* Important Notice */}
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-amber-800">Demande de mise à disposition</p>
                  <p className="text-amber-700">
                    Les produits sont réservés pour retrait en pharmacie. Le paiement se fait sur place.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Cart Items List */}
            <Card className="bg-white border-[#E8DDD4] shadow-sm">
              <CardContent className="p-0 divide-y divide-[#E8DDD4]">
                {cart.map((item) => (
                  <div key={item.id} className="p-4 flex items-center gap-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#FDF8F3] to-[#F5EDE4] rounded-lg flex items-center justify-center flex-shrink-0">
                      <Pill className="w-8 h-8 text-[#C45C26]/40" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-[#5D4E37] truncate">{item.name}</h4>
                      <p className="text-sm text-[#8B7355]">{item.brand}</p>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 border-[#E8DDD4]"
                        onClick={() => updateQuantity(item.id, -1)}
                      >
                        <Minus className="w-4 h-4" />
                      </Button>
                      <span className="w-8 text-center font-medium text-[#5D4E37]">
                        {item.quantity}
                      </span>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 border-[#E8DDD4]"
                        onClick={() => updateQuantity(item.id, 1)}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    <div className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        onClick={() => removeItem(item.id)}
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Retirer
                      </Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Summary */}
          <div className="space-y-4">
            {/* Pharmacy Info */}
            <Card className="bg-white border-[#E8DDD4] shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2 text-[#5D4E37]">
                  <MapPin className="w-5 h-5 text-[#C45C26]" />
                  Retrait en pharmacie
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-[#C45C26] flex-shrink-0 mt-0.5" />
                  <span className="text-[#5D4E37]">{pharmacyInfo.address}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-[#3B82F6]" />
                  <a href={`tel:${pharmacyInfo.phone.replace(/\s/g, '')}`} className="text-[#5D4E37] hover:text-[#C45C26]">
                    {pharmacyInfo.phone}
                  </a>
                </div>
                <div className="flex items-start gap-2">
                  <Clock className="w-4 h-4 text-[#2E7D32] flex-shrink-0 mt-0.5" />
                  <div className="text-[#5D4E37]">
                    <p>Lun-Ven: {pharmacyInfo.hours.weekdays}</p>
                    <p>Sam: {pharmacyInfo.hours.saturday}</p>
                  </div>
                </div>
                <a 
                  href={pharmacyInfo.mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-[#C45C26] hover:underline"
                >
                  <Navigation className="w-4 h-4" />
                  Voir l'itinéraire
                </a>
              </CardContent>
            </Card>

            {/* Récapitulatif */}
            <Card className="bg-white border-[#E8DDD4] shadow-sm">
              <CardContent className="p-4 space-y-4">
                <div className="flex justify-between text-sm">
                  <span className="text-[#8B7355]">Articles à réserver</span>
                  <span className="font-medium text-[#5D4E37]">{totalItems}</span>
                </div>
                <div className="border-t border-[#E8DDD4] pt-3">
                  <p className="text-xs text-[#8B7355] leading-relaxed">
                    Le montant total vous sera communiqué par la pharmacie au moment de la
                    confirmation. Le règlement se fait au comptoir lors du retrait.
                  </p>
                </div>
                
                <Button
                  className="w-full bg-[#C45C26] hover:bg-[#A84B1F]"
                  size="lg"
                  onClick={() => setConfirmDialogOpen(true)}
                  data-testid="checkout-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Envoyer la demande de mise à disposition
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#5D4E37]">Finaliser la demande de mise à disposition</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-[#5D4E37]">Nom complet *</Label>
              <Input
                id="name"
                value={customerInfo.name}
                onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
                placeholder="Votre nom"
                className="mt-1 border-[#E8DDD4] focus:border-[#C45C26]"
              />
            </div>
            
            <div>
              <Label htmlFor="phone" className="text-[#5D4E37]">Téléphone *</Label>
              <Input
                id="phone"
                value={customerInfo.phone}
                onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
                placeholder="07 XX XX XX XX"
                className="mt-1 border-[#E8DDD4] focus:border-[#C45C26]"
              />
            </div>
            
            <div>
              <Label htmlFor="email" className="text-[#5D4E37]">Email (optionnel)</Label>
              <Input
                id="email"
                type="email"
                value={customerInfo.email}
                onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
                placeholder="votre@email.com"
                className="mt-1 border-[#E8DDD4] focus:border-[#C45C26]"
              />
            </div>
            
            <div>
              <Label htmlFor="notes" className="text-[#5D4E37]">Notes (optionnel)</Label>
              <Textarea
                id="notes"
                value={customerInfo.notes}
                onChange={(e) => setCustomerInfo({ ...customerInfo, notes: e.target.value })}
                placeholder="Instructions particulières..."
                className="mt-1 border-[#E8DDD4] focus:border-[#C45C26]"
                rows={2}
              />
            </div>

            <Card className="bg-[#C45C26]/10 border-[#C45C26]/20">
              <CardContent className="p-3">
                <p className="text-sm font-medium text-[#C45C26]">
                  Récapitulatif: {totalItems} article{totalItems > 1 ? 's' : ''} à réserver
                </p>
              </CardContent>
            </Card>
          </div>

          <DialogFooter className="flex-col sm:flex-col gap-2">
            <div className="flex gap-2 w-full">
              <Button variant="outline" onClick={() => setConfirmDialogOpen(false)} className="border-[#E8DDD4] flex-1">
                Annuler
              </Button>
              <Button
                onClick={handleSubmitReservation}
                disabled={submitting || !customerInfo.name || !customerInfo.phone}
                className="bg-[#C45C26] hover:bg-[#A84B1F] flex-1"
              >
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Confirmer la demande
              </Button>
            </div>
            <p
              className="text-[11px] text-[#8B7355] leading-relaxed text-center px-1"
              data-testid="cart-legal-notice"
            >
              Cette demande ne constitue pas une vente en ligne. La mise à disposition
              et la délivrance des produits de santé restent soumises à la validation
              de l'équipe officinale.
            </p>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Success Dialog */}
      <Dialog open={successDialogOpen} onOpenChange={setSuccessDialogOpen}>
        <DialogContent className="max-w-md text-center">
          <div className="w-16 h-16 bg-[#2E7D32]/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-[#2E7D32]" />
          </div>
          <DialogTitle className="text-xl mb-2 text-[#5D4E37]">Demande envoyée !</DialogTitle>
          <p className="text-[#8B7355] mb-4">
            Votre demande de mise à disposition <span className="font-bold text-[#C45C26]">#{reservationId}</span> a été transmise à la pharmacie.
          </p>
          <Card className="bg-[#FDF8F3] border-[#E8DDD4] text-left">
            <CardContent className="p-4 space-y-2 text-sm">
              <p className="font-medium text-[#5D4E37]">Prochaines étapes :</p>
              <ul className="list-disc list-inside text-[#8B7355] space-y-1">
                <li>La pharmacie va préparer votre demande</li>
                <li>Vous recevrez un appel de confirmation</li>
                <li>Présentez-vous avec votre numéro de demande</li>
                <li>Paiement sur place au retrait</li>
              </ul>
            </CardContent>
          </Card>
          <Button
            className="w-full mt-4 bg-[#C45C26] hover:bg-[#A84B1F]"
            onClick={() => {
              setSuccessDialogOpen(false);
              onNavigate('home');
            }}
          >
            Retour à l'accueil
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PharmacyCartPage;
